import React, { useState, useEffect, useCallback } from 'react';
import {useAdminTheme} from '@/contexts/AdminThemeContext';import {Plus, Trash2, Edit2, Eye, EyeOff, ChevronUp, ChevronDown, Save, X, Loader2, Users, CheckCircle, XCircle, BookOpen, MessageSquare, Settings, Layout, Sparkles, Video, Languages, Landmark} from 'lucide-react';import {GospelLensIcon} from '@/components/GospelLensIcon';import {Button} from '@/components/ui/button';import {toast} from 'sonner';import AdminLayout from './AdminLayout';
import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';

const ARROW_OPTIONS = [
  { value: 'top', label: 'Top (arrow points down)' },
  { value: 'bottom', label: 'Bottom (arrow points up)' },
  { value: 'left', label: 'Left (arrow points right)' },
  { value: 'right', label: 'Right (arrow points left)' },
];

const ICON_OPTIONS = [
  { value: 'sparkles', label: 'Sparkles', Icon: Sparkles },
  { value: 'message', label: 'Message', Icon: MessageSquare },
  { value: 'bible', label: 'Bible', Icon: BookOpen },
  { value: 'settings', label: 'Settings', Icon: Settings },
  { value: 'layout', label: 'Layout', Icon: Layout },
  { value: 'video', label: 'YouTube/Video', Icon: Video },
  { value: 'languages', label: 'Languages', Icon: Languages },
  { value: 'users', label: 'Users/Community', Icon: Users },
  { value: 'eye', label: 'Eye', Icon: Eye },
  { value: 'gospel_lens', label: 'Gospel Lens', Icon: GospelLensIcon },
  { value: 'landmark', label: 'Historical Context', Icon: Landmark },
];

const PRESET_SELECTORS = [
  { label: 'Chat Input', value: '[data-testid="message-input"]' },
  { label: 'Send Button', value: '[data-testid="send-message-button"]' },
  { label: 'New Chat Button', value: '[data-testid="new-chat-button"]' },
  { label: 'Bible Reader Button', value: '[data-testid="bible-reader-button"]' },
  { label: 'Settings Button', value: '[data-testid="quick-settings-button"]' },
  { label: 'Sidebar', value: '[data-testid="conversation-sidebar"]' },
  { label: 'Header', value: 'header' },
  { label: 'Custom...', value: '' },
];

export default function AdminTutorialsPage() {
  const { isDark, colors } = useAdminTheme();
  const [steps, setSteps] = useState([]);
  const [config, setConfig] = useState({ enabled: true });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editStep, setEditStep] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchTutorials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tutorials`, { withCredentials: true });
      setSteps(res.data.steps || []);
      setConfig(res.data.config || { enabled: true });
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error('Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTutorials(); }, [fetchTutorials]);

  const toggleEnabled = async () => {
    try {
      await axios.put(`${API}/admin/tutorials/config`, { enabled: !config.enabled }, { withCredentials: true });
      setConfig(c => ({ ...c, enabled: !c.enabled }));
      toast.success(config.enabled ? 'Tutorial disabled' : 'Tutorial enabled');
    } catch { toast.error('Failed to update config'); }
  };

  const deleteStep = async (stepId) => {
    try {
      await axios.delete(`${API}/admin/tutorials/${stepId}`, { withCredentials: true });
      toast.success('Step deleted');
      fetchTutorials();
    } catch { toast.error('Failed to delete'); }
  };

  const moveStep = async (idx, direction) => {
    const newSteps = [...steps];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newSteps.length) return;
    [newSteps[idx], newSteps[swapIdx]] = [newSteps[swapIdx], newSteps[idx]];
    setSteps(newSteps);
    try {
      await axios.put(`${API}/admin/tutorials/reorder`, { step_ids: newSteps.map(s => s.step_id) }, { withCredentials: true });
    } catch { toast.error('Failed to reorder'); fetchTutorials(); }
  };

  const openEdit = (step) => {
    setEditStep(step);
    setShowForm(true);
  };

  const openNew = () => {
    setEditStep(null);
    setShowForm(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl" data-testid="admin-tutorials-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Tutorial Manager</h1>
            <p className="text-sm" style={{ color: colors.textSecondary }}>Create and manage the onboarding tutorial flow</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleEnabled}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors"
              style={{
                background: config.enabled ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                color: config.enabled ? '#10b981' : '#ef4444',
              }}
              data-testid="tutorial-toggle-btn"
            >
              {config.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {config.enabled ? 'Active' : 'Disabled'}
            </button>
            <Button onClick={openNew} className="rounded-xl text-white gap-2 text-xs h-8 px-3" style={{ background: colors.accent }} data-testid="add-tutorial-step-btn">
              <Plus className="h-3.5 w-3.5" /> Add Step
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Users Seen', value: stats.total_users_seen ?? 0, icon: Users, color: '#6366f1' },
            { label: 'Completed', value: stats.total_completions ?? 0, icon: CheckCircle, color: '#22c55e' },
            { label: 'Skipped', value: stats.total_skips ?? 0, icon: XCircle, color: '#f59e0b' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
                <Icon className="h-4 w-4" style={{ color }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: colors.textMuted }}>{label}</p>
                <p className="text-lg font-bold" style={{ color: colors.text }}>{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Steps List */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: colors.textMuted }} />
            </div>
          ) : steps.length === 0 ? (
            <div className="rounded-2xl p-8 text-center" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
              <Sparkles className="h-8 w-8 mx-auto mb-3" style={{ color: colors.textMuted }} />
              <p className="text-sm" style={{ color: colors.textMuted }}>No tutorial steps yet. Add your first step.</p>
            </div>
          ) : steps.map((step, i) => {
            const IconComponent = ICON_OPTIONS.find(o => o.value === step.icon)?.Icon || Sparkles;
            return (
              <div key={step.step_id} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid={`tutorial-step-${i}`}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveStep(i, 'up')} disabled={i === 0} className="p-0.5 rounded transition-colors disabled:opacity-20" style={{ color: colors.textMuted }}>
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => moveStep(i, 'down')} disabled={i === steps.length - 1} className="p-0.5 rounded transition-colors disabled:opacity-20" style={{ color: colors.textMuted }}>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${colors.accent}15` }}>
                  <IconComponent className="h-4 w-4" style={{ color: colors.accent }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${step.highlight_color || colors.accent}15`, color: step.highlight_color || colors.accent }}>Step {i + 1}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: step.mode === 'showcase' ? 'rgba(236,72,153,0.15)' : 'rgba(99,102,241,0.15)', color: step.mode === 'showcase' ? '#ec4899' : '#6366f1' }}>{step.mode || 'spotlight'}</span>
                    <h4 className="text-sm font-medium truncate" style={{ color: colors.text }}>{step.title}</h4>
                  </div>
                  <p className="text-xs mt-0.5 truncate" style={{ color: colors.textMuted }}>{step.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: colors.textMuted, background: colors.input }}>{step.target_selector}</code>
                    <span className="text-[10px]" style={{ color: colors.textMuted }}>arrow: {step.arrow_position}</span>
                    {step.highlight_color && <div className="w-3 h-3 rounded-full" style={{ background: step.highlight_color }} />}
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(step)} className="p-1.5 rounded-lg transition-colors" style={{ color: colors.textSecondary }}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => deleteStep(step.step_id)} className="p-1.5 rounded-lg transition-colors hover:bg-red-500/10 text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Editor Modal */}
        {showForm && (
          <StepFormModal
            step={editStep}
            onClose={() => setShowForm(false)}
            onSave={() => { setShowForm(false); fetchTutorials(); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}


function StepFormModal({ step, onClose, onSave }) {
  const { isDark, colors } = useAdminTheme();
  const [title, setTitle] = useState(step?.title || '');
  const [description, setDescription] = useState(step?.description || '');
  const [targetSelector, setTargetSelector] = useState(step?.target_selector || '');
  const [arrowPosition, setArrowPosition] = useState(step?.arrow_position || 'bottom');
  const [icon, setIcon] = useState(step?.icon || 'sparkles');
  const [mode, setMode] = useState(step?.mode || 'spotlight');
  const [featurePreview, setFeaturePreview] = useState(step?.feature_preview || '');
  const [highlightColor, setHighlightColor] = useState(step?.highlight_color || '#6366f1');
  const [saving, setSaving] = useState(false);
  const [presetSelector, setPresetSelector] = useState('');

  useEffect(() => {
    const match = PRESET_SELECTORS.find(p => p.value === targetSelector);
    setPresetSelector(match ? match.value : '');
  }, [targetSelector]);

  const handleSave = async () => {
    if (!title || !description || !targetSelector) {
      toast.error('Title, description, and target selector are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { title, description, target_selector: targetSelector, arrow_position: arrowPosition, icon, mode, feature_preview: featurePreview || null, highlight_color: highlightColor };
      if (step) {
        await axios.put(`${API}/admin/tutorials/${step.step_id}`, payload, { withCredentials: true });
        toast.success('Step updated');
      } else {
        await axios.post(`${API}/admin/tutorials`, payload, { withCredentials: true });
        toast.success('Step created');
      }
      onSave();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={e => e.target === e.currentTarget && onClose()} data-testid="tutorial-step-form">
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${colors.cardBorder}` }}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ color: colors.text }}>{step ? 'Edit Step' : 'New Tutorial Step'}</h3>
          <button onClick={onClose} className="p-1 rounded-full" style={{ color: colors.textSecondary }}><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="e.g. Ask a Question" data-testid="step-title-input" />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none resize-none" style={inputStyle} placeholder="Explain what this feature does..." data-testid="step-desc-input" />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Target Element</label>
            <select
              value={presetSelector}
              onChange={e => {
                setPresetSelector(e.target.value);
                if (e.target.value) setTargetSelector(e.target.value);
              }}
              className="w-full rounded-xl px-4 py-2.5 text-sm mb-2"
              style={inputStyle}
              data-testid="step-preset-selector"
            >
              <option value="">Select element...</option>
              {PRESET_SELECTORS.map(p => <option key={p.label} value={p.value}>{p.label}</option>)}
            </select>
            {(!presetSelector || presetSelector === '') && (
              <input value={targetSelector} onChange={e => setTargetSelector(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none font-mono text-xs" style={inputStyle} placeholder='CSS selector, e.g. [data-testid="send-button"]' data-testid="step-selector-input" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Arrow Position</label>
              <select value={arrowPosition} onChange={e => setArrowPosition(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="step-arrow-select">
                {ARROW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Icon</label>
              <select value={icon} onChange={e => setIcon(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="step-icon-select">
                {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Mode & Color */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Display Mode</label>
              <select value={mode} onChange={e => setMode(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="step-mode-select">
                <option value="spotlight">Spotlight (highlight element)</option>
                <option value="showcase">Showcase (centered preview)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Accent Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={highlightColor} onChange={e => setHighlightColor(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0" data-testid="step-color-input" />
                <input value={highlightColor} onChange={e => setHighlightColor(e.target.value)} className="flex-1 rounded-xl px-3 py-2.5 text-xs font-mono" style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Feature Preview (for showcase mode) */}
          {mode === 'showcase' && (
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Feature Preview Content</label>
              <textarea
                value={featurePreview} onChange={e => setFeaturePreview(e.target.value)} rows={5}
                className="w-full rounded-xl px-4 py-2.5 text-xs placeholder-slate-500 focus:outline-none resize-none font-mono"
                style={inputStyle}
                placeholder={"**Label Text**\n>>Simulated AI response text\n[]Toggle option label\n--divider\nNormal descriptive text"}
                data-testid="step-preview-input"
              />
              <p className="text-[10px] mt-1" style={{ color: colors.textMuted }}>Formatting: **bold**, &gt;&gt;response bubble, []toggle, --divider</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button onClick={onClose} variant="ghost" className="flex-1 rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 rounded-xl text-white gap-2" style={{ background: colors.accent }} data-testid="step-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {step ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}
